<template>
  <v-content class="gb list-invited-users">
    <v-container fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" md="12" class="top-container">
          <v-card
            :loading="loading"
            class="fill-width table_theme card-theme custom-container"
          >
            <v-card-title class="title_table medium-font" style="letter-spacing: normal">
              Invited Users

              <v-progress-circular
                v-if="loading"
                indeterminate
                color="primary"
                style="padding: 0px 30px;"
              ></v-progress-circular>

              <v-spacer></v-spacer>
              <div class="custom-o-search-bar">
                <v-text-field
                  v-model="search"
                  placeholder="Search by name, phone, or email..."
                  single-line
                  hide-details
                  class="input_search"
                  color="#198beb"
                >
                  <img
                    :src="require('@/assets/img/search.svg')"
                    slot="prepend"
                    alt="search"
                    class="info-tooltip"
                  />
                </v-text-field>
              </div>
            </v-card-title>
            <v-divider class="ma-0 list-divider"></v-divider>

            <!-- Status stats bar -->
            <div class="status-stats-bar">
              <div class="status-stats-bar__item">
                <span class="status-stats-bar__label">Total</span>
                <span class="status-stats-bar__count">{{ invitedUsers.length }}</span>
              </div>
              <div class="status-stats-bar__divider"></div>
              <div
                v-for="(item, index) in statusCounts"
                :key="item.status"
                style="display: flex; align-items: center;"
              >
                <div v-if="index > 0" class="status-stats-bar__divider"></div>
                <div
                  class="status-stats-bar__item"
                  :class="{ 'status-stats-bar__item--active': filters.status === item.status }"
                  @click="toggleStatusFilter(item.status)"
                  style="cursor: pointer;"
                >
                  <v-icon class="status-stats-bar__dot" :style="{ color: statusColor(item.status) }">brightness_1</v-icon>
                  <span class="status-stats-bar__label">{{ item.status }}</span>
                  <span class="status-stats-bar__count">{{ item.count }}</span>
                </div>
              </div>
              <div class="status-stats-bar__actions">
                <v-btn
                  rounded text color="#29ABE2"
                  class="btn-blanco table_btn custom-o-white btn-border action-btn-width"
                  style="text-transform: none;"
                  @click="exportToExcel"
                  :disabled="filteredItems.length === 0"
                >Download (Excel)</v-btn>
              </div>
            </div>

            <v-alert v-if="error" type="error" dense dismissible @input="error = false">
              {{ errorMessage }}
            </v-alert>

            <v-data-table
              :headers="headers"
              :items="filteredItems"
              :search="search"
              :custom-filter="searchFilter"
              color="#717e91"
              item-key="id"
              :options.sync="options"
              class="invited-users-table"
              :mobile-breakpoint="0"
            >
              <template v-slot:item.first_name="{ item }">
                <span class="cell-text">{{ item.first_name || '—' }}</span>
              </template>

              <template v-slot:item.last_name="{ item }">
                <span class="cell-text">{{ item.last_name || '—' }}</span>
              </template>

              <template v-slot:item.phone="{ item }">
                <span class="cell-text">{{ item.phone || '—' }}</span>
              </template>

              <template v-slot:item.email="{ item }">
                <span class="cell-text">{{ item.email || '—' }}</span>
              </template>

              <template v-slot:item.kiey_status="{ item }">
                <v-chip
                  small
                  :color="item.kiey_status === 'Registered' ? '#E8F5E9' : '#FFF3E0'"
                  :text-color="item.kiey_status === 'Registered' ? '#2E7D32' : '#E65100'"
                  style="font-weight: 600; font-size: 11px;"
                >
                  {{ item.kiey_status || 'Not Registered' }}
                </v-chip>
              </template>

              <template v-slot:item.referred_by_name="{ item }">
                <div v-if="item.referred_by_name">
                  <span class="cell-text">{{ item.referred_by_name }}</span>
                  <span v-if="item.referred_by_role" class="cell-subtext">{{ item.referred_by_role }}</span>
                </div>
                <span v-else class="cell-text">—</span>
              </template>

              <template v-slot:item.date_invited="{ item }">
                <span class="cell-text">{{ formatDate(item.date_invited) }}</span>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-content>
</template>

<script>
import axios from "axios";
import config from "@/config/constants.js";

export default {
  name: "InvitedUsersList",
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: "",
      search: "",
      invitedUsers: [],
      filters: {
        status: "",
      },
      options: {
        itemsPerPage: 25,
        sortBy: ["date_invited"],
        sortDesc: [true],
      },
      headers: [
        { text: "FIRST NAME", value: "first_name", sortable: true },
        { text: "LAST NAME", value: "last_name", sortable: true },
        { text: "PHONE", value: "phone", sortable: true, width: "150px" },
        { text: "EMAIL", value: "email", sortable: true },
        { text: "KIEY STATUS", value: "kiey_status", sortable: true, width: "130px" },
        { text: "REFERRED BY", value: "referred_by_name", sortable: true },
        { text: "DATE INVITED", value: "date_invited", sortable: true, width: "130px" },
      ],
    };
  },
  computed: {
    filteredItems() {
      let items = this.invitedUsers;
      if (this.filters.status) {
        items = items.filter(u => u.kiey_status === this.filters.status);
      }
      return items;
    },
    statusCounts() {
      const counts = {};
      this.invitedUsers.forEach(u => {
        const status = u.kiey_status || "Not Registered";
        counts[status] = (counts[status] || 0) + 1;
      });
      const order = ["Registered", "Not Registered"];
      return order
        .filter(s => counts[s])
        .map(s => ({ status: s, count: counts[s] }));
    },
  },
  created() {
    this.$store.dispatch("Banner/show");
    this.fetchInvitedUsers();
  },
  methods: {
    async fetchInvitedUsers() {
      this.loading = true;
      this.error = false;
      try {
        const response = await axios.get(config.api_url + "web/invited-users");
        if (response.data.code === "SUCCESS") {
          this.invitedUsers = response.data.data || [];
        } else {
          this.error = true;
          this.errorMessage = response.data.error || "Failed to load invited users.";
        }
      } catch (err) {
        console.error("fetchInvitedUsers error:", err);
        this.error = true;
        this.errorMessage = "Failed to load invited users.";
      } finally {
        this.loading = false;
      }
    },

    searchFilter(value, search, item) {
      if (!search) return true;
      const s = search.toLowerCase();
      return (
        (item.first_name || "").toLowerCase().includes(s) ||
        (item.last_name || "").toLowerCase().includes(s) ||
        (item.phone || "").toLowerCase().includes(s) ||
        (item.email || "").toLowerCase().includes(s) ||
        (item.referred_by_name || "").toLowerCase().includes(s)
      );
    },

    statusColor(status) {
      if (status === "Registered") return "#4CAF50";
      return "#FF9800";
    },

    toggleStatusFilter(status) {
      this.filters.status = this.filters.status === status ? "" : status;
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    exportToExcel() {
      const items = this.filteredItems;
      if (!items.length) return;

      const headers = ["First Name", "Last Name", "Phone", "Email", "Kiey Status", "Referred By", "Role", "Date Invited"];
      const rows = items.map(u => [
        u.first_name || "",
        u.last_name || "",
        u.phone || "",
        u.email || "",
        u.kiey_status || "Not Registered",
        u.referred_by_name || "",
        u.referred_by_role || "",
        this.formatDate(u.date_invited),
      ]);

      let csv = "\uFEFF" + headers.join(",") + "\n";
      rows.forEach(row => {
        csv += row.map(cell => '"' + String(cell).replace(/"/g, '""') + '"').join(",") + "\n";
      });

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "invited_users.csv";
      link.click();
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style lang="scss" scoped>
.list-invited-users {
  .custom-container {
    max-width: 1222px !important;
    min-height: 400px;
    margin-bottom: 50px !important;
  }

  .cell-text {
    font-size: 13px;
    color: #333;
  }

  .cell-subtext {
    display: block;
    font-size: 11px;
    color: #94A3B8;
    text-transform: capitalize;
  }

  .status-stats-bar__item--active {
    background: #E3F2FD;
    border-radius: 6px;
    padding: 2px 8px;
  }
}
</style>
